import { useNavigate } from "react-router";
import { LoadingSpinner } from "@kubdee/ui/custom/feedback/loading-spinner";
import { useTenantModules } from "@kubdee/sdk/hooks/use-settings";
import { ShieldAlert } from "lucide-react";
import { Button } from "@kubdee/ui/shadcn/button";

interface ModuleGuardProps {
  moduleId: string;
  children: React.ReactNode;
}

/**
 * Guards module routes — redirects to /desk if the module is disabled at tenant level.
 * Shows loading while checking, shows blocked message if disabled.
 * If API fails or no data, allows access (graceful degradation).
 */
export function ModuleGuard({ moduleId, children }: ModuleGuardProps) {
  const { data: tenantModulesRes, isLoading } = useTenantModules();
  const navigate = useNavigate();

  // While loading, show nothing (or a brief spinner)
  // But keep it fast — don't block for too long
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingSpinner size="xl" className="text-muted-foreground" />
      </div>
    );
  }

  const enabledModules = tenantModulesRes?.data ?? [];

  // If no data returned (API error or empty), allow access (graceful degradation)
  if (enabledModules.length === 0) {
    return <>{children}</>;
  }

  // Check if module is enabled
  if (!enabledModules.includes(moduleId)) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground" />
          <h2 className="text-[16px] font-semibold">
            ไม่สามารถเข้าถึงโมดูลนี้
          </h2>
          <p className="text-[13px] text-muted-foreground">
            โมดูลนี้ยังไม่ได้เปิดใช้งานสำหรับองค์กรของคุณ
          </p>
          <Button size="sm" onClick={() => navigate("/desk")}>
            กลับหน้าหลัก
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
